import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import WhatImg from "../../assets/what_we_do.jpg";
import {
  FaRocket,
  FaGlobe,
  FaHandshake,
  FaChartLine,
  FaShip,
  FaTruck,
  FaBoxes,
  FaShieldAlt,
} from "react-icons/fa";

const WhatWeDo = () => {
  return (
    <>
      <Helmet>
        <title>What We Do | Trans World Shipping Company</title>
        <meta
          name="description"
          content="Trans World Shipping Company is a leading provider of logistics and transportation services, offering efficient and reliable solutions for businesses worldwide."
        />
        <link
          rel="canonical"
          href="https://transworldshipping.com/what-we-do"
        />
      </Helmet>

      <TheWhatWeDo>
        {/* Hero Section */}
        <div className="what-hero">
          <div className="hero-overlay"></div>
          <img src={WhatImg} alt="Global logistics and shipping" />
          <div className="hero-content">
            <h1>What We Do</h1>
            <p>Setting the standard for excellence in global logistics</p>
          </div>
        </div>

        {/* Mission Section */}
        <div className="mission-section">
          <div className="container">
            <div className="mission-header">
              <h2>Our Mission</h2>
              <div className="header-line"></div>
            </div>
            <div className="mission-content">
              <p>
                Transworld sets the standard for excellence in global logistics
                through loyalty and commitment to quality in people and customer
                service, with superior cost-effective results. We provide much
                more than simply getting a piece of freight through the logistic
                chain process.
              </p>
            </div>
          </div>
        </div>

        {/* Definition Section */}
        <div className="definition-section">
          <div className="container">
            <div className="definition-grid">
              <div className="definition-text">
                <h3>Understanding Modern Logistics</h3>
                <p>
                  The Council of Logistics Management defines logistics as the
                  part of the supply chain process that plans, implements, and
                  controls the efficient, effective flow and storage of goods,
                  services, and related information from the point of origin to
                  the point of consumption.
                </p>
                <p className="highlight">
                  Our job is to ensure that, from raw material to retailers, we
                  provide the essential services and information necessary to
                  provide our clients with a competitive advantage.
                </p>
              </div>
              <div className="definition-stats">
                <div className="stat-item">
                  <span className="stat-number">40+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">25+</span>
                  <span className="stat-label">Countries Served</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">50K+</span>
                  <span className="stat-label">Shipments Yearly</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="values-section">
          <div className="container">
            <div className="values-header">
              <h2>Our Core Values</h2>
              <p>What drives us to deliver excellence every day</p>
              <div className="header-line"></div>
            </div>
            <div className="values-grid">
              <div className="value-card">
                <FaRocket className="value-icon" />
                <h4>Innovation</h4>
                <p>
                  Continuously improving our processes and technology to serve
                  you better
                </p>
              </div>
              <div className="value-card">
                <FaHandshake className="value-icon" />
                <h4>Integrity</h4>
                <p>
                  Building trust through transparent and honest business
                  practices
                </p>
              </div>
              <div className="value-card">
                <FaGlobe className="value-icon" />
                <h4>Global Reach</h4>
                <p>
                  Connecting businesses worldwide with seamless logistics
                  solutions
                </p>
              </div>
              <div className="value-card">
                <FaChartLine className="value-icon" />
                <h4>Excellence</h4>
                <p>
                  Setting the standard for quality and reliability in every
                  shipment
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Overview */}
        <div className="services-overview">
          <div className="container">
            <div className="services-header">
              <h2>Comprehensive Solutions</h2>
              <p>End-to-end logistics services tailored to your needs</p>
              <div className="header-line"></div>
            </div>
            <div className="services-grid">
              <div className="service-item">
                <FaShip className="service-icon" />
                <h4>Ocean Freight</h4>
                <p>
                  FCL, LCL, and specialized ocean freight solutions worldwide
                </p>
              </div>
              <div className="service-item">
                <FaTruck className="service-icon" />
                <h4>Land Transport</h4>
                <p>Intermodal and door-to-door delivery solutions</p>
              </div>
              <div className="service-item">
                <FaBoxes className="service-icon" />
                <h4>Warehousing</h4>
                <p>
                  Strategic distribution centers with advanced inventory
                  management
                </p>
              </div>
              <div className="service-item">
                <FaShieldAlt className="service-icon" />
                <h4>Customs Clearance</h4>
                <p>Expert handling of international trade compliance</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="container">
            <h3>Ready to streamline your supply chain?</h3>
            <p>Partner with us and experience the Transworld difference</p>
            <button className="cta-button">Request a Quote</button>
          </div>
        </div>
      </TheWhatWeDo>
    </>
  );
};

const TheWhatWeDo = styled.div`
  width: 100%;
  background: #ffffff;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  /* Hero Section */
  .what-hero {
    position: relative;
    width: 100%;
    height: 60vh;
    min-height: 400px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        135deg,
        rgba(11, 30, 51, 0.85) 0%,
        rgba(0, 0, 0, 0.6) 100%
      );
      z-index: 1;
    }

    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      z-index: 2;
      width: 100%;
      padding: 0 2rem;

      h1 {
        font-size: 3.5rem;
        font-weight: 700;
        color: white;
        margin-bottom: 1rem;
        letter-spacing: -1px;
      }

      p {
        font-size: 1.2rem;
        color: rgba(255, 255, 255, 0.9);
        font-weight: 400;
      }
    }
  }

  /* Mission Section */
  .mission-section {
    padding: 5rem 0;
    background: #f8f9fa;

    .mission-header {
      text-align: center;
      margin-bottom: 2.5rem;

      h2 {
        font-size: 2.5rem;
        color: #0b1e33;
        margin-bottom: 1rem;
        font-weight: 700;
      }

      .header-line {
        width: 70px;
        height: 3px;
        background: #d4af37;
        margin: 0 auto;
        border-radius: 2px;
      }
    }

    .mission-content {
      max-width: 900px;
      margin: 0 auto;
      text-align: center;

      p {
        font-size: 1.1rem;
        line-height: 1.8;
        color: #2d3748;
        font-weight: 500;
      }
    }
  }

  /* Definition Section */
  .definition-section {
    padding: 5rem 0;
    background: white;

    .definition-grid {
      display: grid;
      grid-template-columns: 1fr 0.6fr;
      gap: 3rem;
      align-items: center;

      .definition-text {
        h3 {
          font-size: 1.8rem;
          color: #0b1e33;
          margin-bottom: 1.5rem;
          font-weight: 700;
        }

        p {
          font-size: 1rem;
          line-height: 1.7;
          color: #4a5568;
          margin-bottom: 1.5rem;

          &.highlight {
            color: #d4af37;
            font-weight: 600;
            font-size: 1.05rem;
            border-left: 3px solid #d4af37;
            padding-left: 1rem;
          }
        }
      }

      .definition-stats {
        background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
        padding: 2rem;
        border-radius: 16px;
        text-align: center;

        .stat-item {
          margin-bottom: 2rem;

          &:last-child {
            margin-bottom: 0;
          }

          .stat-number {
            display: block;
            font-size: 2.5rem;
            font-weight: 800;
            color: #d4af37;
            margin-bottom: 0.5rem;
          }

          .stat-label {
            font-size: 0.9rem;
            color: white;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
        }
      }
    }
  }

  /* Values Section */
  .values-section {
    padding: 5rem 0;
    background: #f8f9fa;

    .values-header {
      text-align: center;
      margin-bottom: 3rem;

      h2 {
        font-size: 2.5rem;
        color: #0b1e33;
        margin-bottom: 0.5rem;
        font-weight: 700;
      }

      p {
        font-size: 1.1rem;
        color: #4a5568;
        margin-bottom: 1.5rem;
      }

      .header-line {
        width: 70px;
        height: 3px;
        background: #d4af37;
        margin: 0 auto;
        border-radius: 2px;
      }
    }

    .values-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;

      .value-card {
        background: white;
        padding: 2rem;
        text-align: center;
        border-radius: 12px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .value-icon {
          font-size: 2.5rem;
          color: #d4af37;
          margin-bottom: 1rem;
        }

        h4 {
          font-size: 1.3rem;
          color: #0b1e33;
          margin-bottom: 0.75rem;
          font-weight: 700;
        }

        p {
          font-size: 0.9rem;
          color: #718096;
          line-height: 1.5;
        }
      }
    }
  }

  /* Services Overview */
  .services-overview {
    padding: 5rem 0;
    background: white;

    .services-header {
      text-align: center;
      margin-bottom: 3rem;

      h2 {
        font-size: 2.5rem;
        color: #0b1e33;
        margin-bottom: 0.5rem;
        font-weight: 700;
      }

      p {
        font-size: 1.1rem;
        color: #4a5568;
        margin-bottom: 1.5rem;
      }

      .header-line {
        width: 70px;
        height: 3px;
        background: #d4af37;
        margin: 0 auto;
        border-radius: 2px;
      }
    }

    .services-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;

      .service-item {
        text-align: center;
        padding: 2rem;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          border-color: #d4af37;
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
        }

        .service-icon {
          font-size: 2rem;
          color: #d4af37;
          margin-bottom: 1rem;
        }

        h4 {
          font-size: 1.2rem;
          color: #0b1e33;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        p {
          font-size: 0.85rem;
          color: #718096;
          line-height: 1.5;
        }
      }
    }
  }

  /* CTA Section */
  .cta-section {
    padding: 4rem 0;
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
    text-align: center;

    h3 {
      font-size: 2rem;
      color: white;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }

    p {
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 2rem;
    }

    .cta-button {
      background: #d4af37;
      color: #0b1e33;
      border: none;
      padding: 0.9rem 2rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      font-family: inherit;

      &:hover {
        background: #c4a12e;
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
      }
    }
  }

  /* Responsive Breakpoints - Cards wrap naturally */
  @media screen and (max-width: 1366px) {
    .container {
      max-width: 1100px;
    }
  }

  @media screen and (max-width: 1200px) {
    .definition-section .definition-grid {
      gap: 2rem;
    }

    .values-grid,
    .services-grid {
      gap: 1.5rem;
    }
  }

  @media screen and (max-width: 1024px) {
    .what-hero .hero-content h1 {
      font-size: 2.8rem;
    }

    .values-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .services-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media screen and (max-width: 900px) {
    .mission-section,
    .definition-section,
    .values-section,
    .services-overview {
      padding: 3rem 0;
    }

    .what-hero .hero-content h1 {
      font-size: 2.5rem;
    }

    .definition-section .definition-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }

    .definition-stats {
      max-width: 100%;
      margin: 0 auto;
      width: 100%;
    }

    .values-header h2,
    .services-header h2,
    .mission-header h2 {
      font-size: 2rem;
    }
  }

  @media screen and (max-width: 768px) {
    .container {
      padding: 0 1.5rem;
    }

    .what-hero {
      height: 50vh;
      min-height: 350px;
    }

    .what-hero .hero-content h1 {
      font-size: 2rem;
    }

    .what-hero .hero-content p {
      font-size: 1rem;
    }

    .mission-content p {
      font-size: 1rem;
    }

    .definition-text h3 {
      font-size: 1.5rem;
    }

    .values-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
    }

    .services-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
    }

    .cta-section h3 {
      font-size: 1.5rem;
    }
  }

  @media screen and (max-width: 600px) {
    .container {
      padding: 0 1rem;
    }

    .what-hero .hero-content h1 {
      font-size: 1.8rem;
    }

    .mission-header h2,
    .values-header h2,
    .services-header h2 {
      font-size: 1.8rem;
    }

    .definition-text h3 {
      font-size: 1.3rem;
    }

    .definition-stats .stat-item .stat-number {
      font-size: 2rem;
    }

    /* Single column on mobile - cards stack vertically */
    .values-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .services-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .value-card,
    .service-item {
      padding: 1.5rem;
      width: 100%;
      box-sizing: border-box;
    }
  }

  @media screen and (max-width: 480px) {
    .container {
      padding: 0 1rem;
    }

    .what-hero {
      height: 40vh;
      min-height: 300px;
    }

    .what-hero .hero-content h1 {
      font-size: 1.5rem;
    }

    .what-hero .hero-content p {
      font-size: 0.9rem;
    }

    .mission-section .mission-content p {
      font-size: 0.95rem;
    }

    .cta-section h3 {
      font-size: 1.3rem;
    }

    .cta-section p {
      font-size: 0.95rem;
    }

    .cta-button {
      padding: 0.75rem 1.5rem;
      font-size: 0.9rem;
    }
  }

  @media screen and (max-width: 350px) {
    .container {
      padding: 0 1rem;
    }

    .what-hero .hero-content h1 {
      font-size: 1.3rem;
    }

    .mission-header h2,
    .values-header h2,
    .services-header h2 {
      font-size: 1.5rem;
    }

    .value-card h4,
    .service-item h4 {
      font-size: 1.1rem;
    }

    .value-card p,
    .service-item p {
      font-size: 0.85rem;
    }
  }
`;

export default WhatWeDo;
